/**
 * Platform Permissions Utility
 * 
 * Handles special platform-level permissions that control behavior and data access,
 * separate from API endpoint permissions.
 */

export interface PlatformPermissions {
    canAccessOlderDataThanLimit: boolean;
    canAccessUnanonymizedIndex: boolean;
}

/**
 * Platform permission constants
 */
export const PLATFORM_PERMISSIONS = {
    CAN_ACCESS_OLDER_DATA_THAN_LIMIT: 'fs.event.override_time_limited_access.execute',
    CAN_ACCESS_UNANONYMIZED_INDEX: 'fs.event.original_data.read',
} as const;

/**
 * Extract platform permissions from user permissions array
 */
export function getPlatformPermissions(userPermissions: string[] | undefined): PlatformPermissions {
    if (!userPermissions) {
        return {
            canAccessOlderDataThanLimit: false,
            canAccessUnanonymizedIndex: false,
        };
    }

    return {
        canAccessOlderDataThanLimit: userPermissions.includes(PLATFORM_PERMISSIONS.CAN_ACCESS_OLDER_DATA_THAN_LIMIT),
        canAccessUnanonymizedIndex: userPermissions.includes(PLATFORM_PERMISSIONS.CAN_ACCESS_UNANONYMIZED_INDEX),
    };
}

/**
 * Check if user has a specific platform permission
 */
export function hasPlatformPermission(
    userPermissions: string[] | undefined, 
    permission: keyof typeof PLATFORM_PERMISSIONS
): boolean {
    if (!userPermissions) {return false;}
    return userPermissions.includes(PLATFORM_PERMISSIONS[permission]);
}

/**
 * Hook to get platform permissions from user data
 */
export function usePlatformPermissions(userData: any): PlatformPermissions {
    return getPlatformPermissions(userData?.permissions);
}
